/**
 * Central query key factory. Every feature's hooks import from here instead
 * of hand-rolling key arrays, so invalidation stays consistent as the app
 * grows (e.g. `qk.weeks.all()` invalidates every week-scoped query).
 */
export const qk = {
  session: () => ["session"] as const,

  tenant: {
    public: (slug: string) => ["tenant", "public", slug] as const,
  },

  platform: {
    all: () => ["platform"] as const,
    tenants: () => [...qk.platform.all(), "tenants"] as const,
    metrics: (from: string, to: string) => [...qk.platform.all(), "metrics", from, to] as const,
    cardUsage: (tenantId: string, from: string, to: string) =>
      [...qk.platform.all(), "cardUsage", tenantId, from, to] as const,
    globalHealth: () => [...qk.platform.all(), "globalHealth"] as const,
    audit: (tenantId: string | null) => [...qk.platform.all(), "audit", tenantId] as const,
    syncLog: (tenantId: string | null, status: string | null) =>
      [...qk.platform.all(), "syncLog", tenantId, status] as const,
    support: () => [...qk.platform.all(), "support"] as const,
    admins: (tenantId: string) => [...qk.platform.all(), "admins", tenantId] as const,
  },

  companies: {
    all: () => ["companies"] as const,
    list: () => [...qk.companies.all(), "list"] as const,
    listWithCardCounts: () => [...qk.companies.all(), "listWithCardCounts"] as const,
    detail: (id: number) => [...qk.companies.all(), "detail", id] as const,
  },

  cards: {
    all: () => ["cards"] as const,
    list: (filter: {
      cardNumbers?: string[];
      searchType?: string;
      searchParam?: string;
    }) => [...qk.cards.all(), "list", filter] as const,
    page: (filter: { cardNumbers?: string[]; statuses: readonly string[]; search: string }) =>
      [...qk.cards.all(), "page", filter] as const,
    detail: (cardNumber: string) => [...qk.cards.all(), "detail", cardNumber] as const,
    assignment: (cardNumber: string) => [...qk.cards.all(), "assignment", cardNumber] as const,
    companyCardNumbers: (companyId: number) =>
      [...qk.cards.all(), "companyCardNumbers", companyId] as const,
    companyAssignments: (companyId: number) =>
      [...qk.cards.all(), "companyAssignments", companyId] as const,
    companyDrivers: (companyId: number) => [...qk.cards.all(), "companyDrivers", companyId] as const,
    driverCards: (driverEmail: string) => [...qk.cards.all(), "driverCards", driverEmail] as const,
    assignedNumbers: () => [...qk.cards.all(), "assignedNumbers"] as const,
    companyMappings: () => [...qk.cards.all(), "companyMappings"] as const,
  },

  cardStatusEvents: {
    all: () => ["cardStatusEvents"] as const,
    list: (cardNumber: string, scope: "all" | { companyId: number }) =>
      [...qk.cardStatusEvents.all(), "list", cardNumber, scope] as const,
  },

  cardRejections: {
    all: () => ["cardRejections"] as const,
    list: (cardNumber: string) => [...qk.cardRejections.all(), "list", cardNumber] as const,
  },

  cardNotes: {
    all: () => ["cardNotes"] as const,
    list: (companyId: number, cardNumber: string) =>
      [...qk.cardNotes.all(), "list", companyId, cardNumber] as const,
  },

  transactions: {
    all: () => ["transactions"] as const,
    card: (cardNumber: string) => [...qk.transactions.all(), "card", cardNumber] as const,
  },

  weeks: {
    all: () => ["weeks"] as const,
    list: () => [...qk.weeks.all(), "list"] as const,
    detail: (id: number) => [...qk.weeks.all(), "detail", id] as const,
    reports: (weekId: number) => [...qk.weeks.all(), "reports", weekId] as const,
  },

  companyReports: {
    all: () => ["companyReports"] as const,
    range: (companyId: number, fromIso: string, toIso: string) =>
      [...qk.companyReports.all(), companyId, fromIso, toIso] as const,
  },

  monthlyReports: {
    all: () => ["monthlyReports"] as const,
    list: () => [...qk.monthlyReports.all(), "list"] as const,
    detail: (id: number) => [...qk.monthlyReports.all(), "detail", id] as const,
    reports: (monthId: number) => [...qk.monthlyReports.all(), "reports", monthId] as const,
  },

  /** Deliberately outside `moneyCodes.all()` — the sync invalidates that subtree when it finishes, which must not refetch the sync itself. */
  /** Outside `cards.all()` for the same reason as `moneyCodeSync`. */
  cardSync: () => ["cardSync"] as const,
  moneyCodeSync: () => ["moneyCodeSync"] as const,
  /** Outside `cardRejections.all()` for the same reason as `moneyCodeSync`. */
  cardRejectionSync: (cardNumber: string) => ["cardRejectionSync", cardNumber] as const,
  moneyCodes: {
    all: () => ["moneyCodes"] as const,
    list: (filter: string) => [...qk.moneyCodes.all(), "list", filter] as const,
    usage: (companyId: number) => [...qk.moneyCodes.all(), "usage", companyId] as const,
    fleetUsage: () => [...qk.moneyCodes.all(), "fleetUsage"] as const,
    drivers: (companyId: number) => [...qk.moneyCodes.all(), "drivers", companyId] as const,
  },

  settings: {
    all: () => ["settings"] as const,
    discount: (companyId: number) => [...qk.settings.all(), "discount", companyId] as const,
    weeklyLimit: (companyId: number) => [...qk.settings.all(), "weeklyLimit", companyId] as const,
  },

  appSettings: {
    all: () => ["appSettings"] as const,
    reportCc: () => [...qk.appSettings.all(), "reportCc"] as const,
  },

  revenue: {
    all: () => ["revenue"] as const,
    cardFees: (monthKey: string) => [...qk.revenue.all(), "cardFees", monthKey] as const,
    scrapedDiscount: (weekKey: string) => [...qk.revenue.all(), "scrapedDiscount", weekKey] as const,
  },

  map: {
    locations: () => ["map", "locations"] as const,
    citySearch: (query: string) => ["map", "citySearch", query] as const,
    addressSearch: (query: string) => ["map", "addressSearch", query] as const,
    driverCompanyId: (driverEmail: string) => ["map", "driverCompanyId", driverEmail] as const,
    route: (fromKey: string, toKey: string) => ["map", "route", fromKey, toKey] as const,
    routeLocations: (fromKey: string, toKey: string) => ["map", "routeLocations", fromKey, toKey] as const,
  },
} as const;
